import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import useAuthStore from '../store/authStore';
import { reportFrontendError } from '../utils/errorReporting';
import { waitForTokenRotatedElsewhere } from '../utils/tokenRotation';

// Inside a Capacitor native shell, `window.location` points at the app's own
// local webview (capacitor://localhost, or https://localhost on Android) —
// it has no relationship to the developer machine, so the web-only
// window.location heuristic below would silently resolve to the wrong host.
// These are just reasonable *development* defaults for emulators/simulators;
// a real device or shipped build must set REACT_APP_API_URL at build time to
// the backend's actual reachable address (LAN IP or public domain).
function resolveNativeDevDefault() {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') {
    // The Android emulator maps 10.0.2.2 to the host machine's loopback.
    return 'http://10.0.2.2:8000/api';
  }
  // The iOS simulator shares the host's network stack, so localhost works.
  return 'http://localhost:8000/api';
}

// Resolve API base URL with priority:
// 1. REACT_APP_API_URL or VITE_API_URL (build-time env) — required for any
//    real device build or production deployment.
// 2. Native (Capacitor) dev default for the current platform's simulator/emulator.
// 3. Web: derive from window.location (useful when frontend is served from
//    browser and backend is on the same host, e.g. the Docker dev setup).
function resolveApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL || process.env.VITE_API_URL;
  if (Capacitor.isNativePlatform()) {
    return envUrl || resolveNativeDevDefault();
  }

  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    // Every production school (L'mcid subdomain or connected custom domain)
    // exposes its tenant-aware API on the same origin. A compile-time
    // lmcid.com API URL would erase the Host header and route every school
    // into the primary database.
    const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local') ||
      /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) || host.includes(':');
    if (!isLocalDev && (protocol === 'https:' || protocol === 'http:')) {
      return `${window.location.origin}/api`;
    }
    if (envUrl) return envUrl;
    // This dev setup always runs the backend on :8000, separate from the
    // frontend's own port (3000) — true whether accessed via localhost or a
    // LAN IP, so the same rule applies to both instead of just localhost.
    return `${protocol}//${host}:8000/api`;
  }

  // Fallback
  return 'http://localhost:8000/api';
}

const API_BASE_URL = resolveApiBaseUrl();

// Same resolution logic, but returning a ws(s):// origin (no path) for the
// messaging WebSocket connections. Exported so Sidebar/Messages share one
// implementation instead of re-deriving this in three places.
export function getWsOrigin() {
  const envUrl = process.env.REACT_APP_API_URL || process.env.VITE_API_URL;
  if (Capacitor.isNativePlatform() && envUrl) {
    try {
      const u = new URL(envUrl);
      const wsProtocol = u.protocol === 'https:' ? 'wss' : 'ws';
      return `${wsProtocol}://${u.host}`;
    } catch (e) {
      // fall through to the platform defaults below
    }
  }

  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    return platform === 'android' ? 'ws://10.0.2.2:8000' : 'ws://localhost:8000';
  }

  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local') ||
      /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) || host.includes(':');
    if (!isLocalDev) return `${protocol}://${window.location.host}`;
    if (envUrl) {
      try {
        const u = new URL(envUrl);
        return `${u.protocol === 'https:' ? 'wss' : 'ws'}://${u.host}`;
      } catch (e) {}
    }
    // Same reasoning as resolveApiBaseUrl: the backend is always on :8000 in
    // this dev setup, regardless of which host/IP was used to reach the app.
    return `${protocol}://${host}:8000`;
  }

  return 'ws://localhost:8000';
}

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// The backend rotates refresh tokens (SIMPLE_JWT ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION): each successful refresh issues a new refresh
// token and blacklists the old one. If several requests 401 around the same
// moment (e.g. resuming a long-idle tab, or a multi-step submit like quiz
// creation firing many sequential calls right as the token expires), each
// one independently calling /auth/refresh/ is a race — the first rotates
// the token, so every other concurrent attempt is refreshing with an
// already-blacklisted token and fails, which used to force a hard
// window.location redirect that wiped whatever the user was mid-typing.
// Sharing a single in-flight refresh promise across all of them avoids the
// race entirely: only one real refresh call ever goes out at a time.
let refreshPromise = null;

// A second open tab (or a native app instance alongside the browser) has its
// own refreshPromise and can still race this one across processes, sending
// the same soon-to-be-blacklisted refresh token at nearly the same moment -
// see waitForTokenRotatedElsewhere for how that's recovered from.
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token available');

      try {
        const response = await axios.post(`${API_BASE_URL}/users/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        // Keep the Zustand store's copies in sync, not just the raw
        // localStorage keys, so components reading these via useAuthStore()
        // don't act on stale values after a silent refresh. Saving the
        // rotated refresh token matters even more than the access token:
        // the one we just sent is now blacklisted, so without this every
        // session's *next* silent refresh would fail outright.
        useAuthStore.getState().setAccessToken(access);
        if (refresh) useAuthStore.getState().setRefreshToken(refresh);
        return access;
      } catch (error) {
        if (error.response?.status === 401) {
          const recovered = await waitForTokenRotatedElsewhere(refreshToken);
          if (recovered) return recovered;
        }
        throw error;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response && originalRequest && !originalRequest.url?.includes('/monitoring/frontend-errors/')) {
      reportFrontendError({
        category: 'frontend_network',
        code: error.code || 'NETWORK_ERROR',
        description: error.message || 'The browser could not reach the API.',
        path: originalRequest.url,
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/')) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// The DRF pagination on this backend ignores `?page_size=`, so a caller that
// needs the *whole* list (e.g. to bucket records by date for a trend chart)
// has to walk `next` itself. Capped at 5 pages (500 rows at the default
// page size of 20) so a genuinely huge account can't hang a dashboard.
export async function fetchAllPages(url, params = {}, maxPages = 5) {
  let results = [];
  let next = url;
  let nextParams = params;
  for (let i = 0; i < maxPages && next; i += 1) {
    const res = await api.get(next, i === 0 ? { params: nextParams } : undefined);
    const data = res.data;
    results = results.concat(data.results || data || []);
    next = data.next || null;
    nextParams = undefined;
  }
  return results;
}

// API Services
export const authService = {
  login: (email, password) => api.post('/users/auth/login/', { email, password }),
  register: (userData) => api.post('/users/auth/register/', userData),
  // Best-effort: blacklists the refresh token server-side (so it can't be
  // silently reused/refreshed later) and closes the matching session record
  // shown on the admin Connections page. Never throws - the caller always
  // clears local storage regardless of whether this network call succeeds.
  logout: async () => {
    const refresh = localStorage.getItem('refreshToken');
    if (refresh) {
      try {
        await api.post('/users/auth/logout/', { refresh });
      } catch (e) {
        // Swallow - logging out client-side must always succeed.
      }
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },
  getCurrentUser: () => api.get('/users/me/'),
  // Leave Content-Type unset for FormData. The browser supplies it together
  // with the generated multipart boundary; a hand-written bare multipart
  // header can reach Django without a usable boundary in some clients.
  updateProfile: (formData, onUploadProgress) => api.patch('/users/me/', formData, { onUploadProgress }),
  verifyEmail: (token) => api.post('/users/auth/verify-email/', { token }),
  resendVerification: () => api.post('/users/auth/resend-verification/'),
  requestPasswordReset: (email) => api.post('/users/auth/password-reset/', { email }),
  confirmPasswordReset: (uid, token, newPassword) => api.post('/users/auth/password-reset-confirm/', { uid, token, new_password: newPassword }),
};

export const academicService = {
  getGrades: (params) => api.get('/academics/grades/', { params }),
  getGrade: (id) => api.get(`/academics/grades/${id}/`),
  allocateGradeStudents: (id, studentIds) => api.post(`/academics/grades/${id}/allocate_students/`, { student_ids: studentIds }),
  allocateGradeCourses: (id, moduleIds) => api.post(`/academics/grades/${id}/allocate_courses/`, { module_ids: moduleIds }),
  getModules: (params) => api.get('/academics/modules/', { params }),
  getMyModules: () => api.get('/academics/modules/my/'),
  getModule: (id) => api.get(`/academics/modules/${id}/`),
  createModule: (data) => api.post('/academics/modules/', data),
  updateModule: (id, data, config) => api.patch(`/academics/modules/${id}/`, data, config),
  deleteModule: (id) => api.delete(`/academics/modules/${id}/`),
  getModuleStatistics: (id) => api.get(`/academics/modules/${id}/statistics/`),
  getModuleDiscussionSummary: (id) => api.get(`/academics/modules/${id}/discussion-summary/`),
  getCategories: (moduleId) => api.get(`/academics/categories/?module=${moduleId}`),
  getCategory: (id) => api.get(`/academics/categories/${id}/`),
  createCategory: (data) => api.post('/academics/categories/', data),
  updateCategory: (id, data) => api.patch(`/academics/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/academics/categories/${id}/`),
  createGrade: (data) => api.post('/academics/grades/', data),
  updateGrade: (id, data) => api.patch(`/academics/grades/${id}/`, data),
  deleteGrade: (id) => api.delete(`/academics/grades/${id}/`),
  // Sponsorships
  getSponsorships: (params) => api.get('/academics/sponsorships/', { params }),
  createSponsorship: (data) => api.post('/academics/sponsorships/', data),
  updateSponsorship: (id, data) => api.patch(`/academics/sponsorships/${id}/`, data),
  deleteSponsorship: (id) => api.delete(`/academics/sponsorships/${id}/`),
  approveSponsorship: (id) => api.post(`/academics/sponsorships/${id}/approve/`),
  rejectSponsorship: (id, reason) => api.post(`/academics/sponsorships/${id}/reject/`, { reason }),
};

export const contentService = {
  getLessons: (categoryId) => api.get(`/content/lessons/?category=${categoryId}`),
  getLesson: (id) => api.get(`/content/lessons/${id}/`),
  getAttachments: (lessonId) => api.get(`/content/attachments/?lesson=${lessonId}`),
  createLesson: (data, config) => api.post('/content/lessons/', data, config),
  updateLesson: (id, data, config) => api.patch(`/content/lessons/${id}/`, data, config),
  publishLesson: (id) => api.post(`/content/lessons/${id}/publish/`),
  deleteLesson: (id) => api.delete(`/content/lessons/${id}/`),
  deleteAttachment: (id) => api.delete(`/content/attachments/${id}/`),
};

export const quizService = {
  getQuizzes: (lessonId) => api.get('/quizzes/quizzes/', { params: lessonId ? { lesson: lessonId } : undefined }),
  getModuleQuizzes: (moduleId) => api.get('/quizzes/quizzes/', { params: { module: moduleId, page_size: 100 } }),
  getQuiz: (id) => api.get(`/quizzes/quizzes/${id}/`),
  submitQuizAttempt: (attemptData) => api.post('/quizzes/attempts/', attemptData),
  getMyAttempts: () => api.get('/quizzes/attempts/'),
  getAttempt: (id) => api.get(`/quizzes/attempts/${id}/`),
  updateQuiz: (id, data) => api.patch(`/quizzes/quizzes/${id}/`, data),
  deleteQuiz: (id) => api.delete(`/quizzes/quizzes/${id}/`),
};

export const questionService = {
  createQuestion: (data, isFormData) => api.post('/quizzes/questions/', data, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
  updateQuestion: (id, data) => api.patch(`/quizzes/questions/${id}/`, data),
  deleteQuestion: (id) => api.delete(`/quizzes/questions/${id}/`),
};

export const answerService = {
  createAnswer: (data) => api.post('/quizzes/answers/', data),
  updateAnswer: (id, data) => api.patch(`/quizzes/answers/${id}/`, data),
  deleteAnswer: (id) => api.delete(`/quizzes/answers/${id}/`),
};

export const enrollmentService = {
  getSubscriptions: (params) => api.get('/enrollments/subscriptions/', { params }),
  createSubscription: (data) => api.post('/enrollments/subscriptions/', data),
  updateSubscription: (id, data) => api.patch(`/enrollments/subscriptions/${id}/`, data),
  deleteSubscription: (id) => api.delete(`/enrollments/subscriptions/${id}/`),
  requestEnrollment: (enrollmentData) => api.post('/enrollments/enrollments/', enrollmentData),
  getMyEnrollments: () => api.get('/enrollments/enrollments/'),
  // Generic paginated/filterable list - the backend already scopes results
  // by role (admin sees everything, a teacher only their own courses), so
  // this one function serves both the admin and teacher Enrollments pages.
  getEnrollments: (params) => api.get('/enrollments/enrollments/', { params }),
  getModuleEnrollments: (moduleId, status) => api.get(`/enrollments/enrollments/?module=${moduleId}${status ? `&status=${status}` : ''}`),
  getTeacherStudents: (params) => api.get('/enrollments/enrollments/', {
    params: { ...params, view: 'teacher_students', status: 'active' },
  }),
  getTeacherComments: (enrollmentId) => api.get(`/enrollments/enrollments/${enrollmentId}/teacher-comments/`),
  addTeacherComment: (enrollmentId, content) => api.post(`/enrollments/enrollments/${enrollmentId}/teacher-comments/`, { content }),
  deleteTeacherComment: (enrollmentId, commentId) => api.delete(`/enrollments/enrollments/${enrollmentId}/teacher-comments/${commentId}/`),
  approveEnrollment: (id, data) => api.post(`/enrollments/enrollments/${id}/approve/`, data),
  rejectEnrollment: (id, reason) => api.post(`/enrollments/enrollments/${id}/reject/`, { reason }),
  requestCertificate: (id) => api.post(`/enrollments/enrollments/${id}/request-certificate/`),
  approveCertificate: (id) => api.post(`/enrollments/enrollments/${id}/approve-certificate/`),
  downloadCertificate: (id) => api.get(`/enrollments/enrollments/${id}/certificate/`, { responseType: 'blob' }),
  // Notifications
  getNotifications: (params) => api.get('/enrollments/notifications/', { params }),
  getUnreadNotificationsCount: () => api.get('/enrollments/notifications/unread_count/'),
  markNotificationRead: (id) => api.post(`/enrollments/notifications/${id}/mark_read/`),
  getBadgeSummary: () => api.get('/enrollments/notifications/badge_summary/'),
  // Events
  getEvents: (params) => api.get('/enrollments/events/', { params }),
  createEvent: (data) => api.post('/enrollments/events/', data),
  updateEvent: (id, data) => api.patch(`/enrollments/events/${id}/`, data),
  deleteEvent: (id, series = false) => api.delete(`/enrollments/events/${id}/${series ? '?series=true' : ''}`),
  rsvpEvent: (id, status) => api.post(`/enrollments/events/${id}/rsvp/`, { status }),
  downloadEventIcs: (id) => api.get(`/enrollments/events/${id}/ics/`, { responseType: 'blob' }),
  getEventAttendance: (eventId) => api.get(`/enrollments/events/${eventId}/attendance/`),
  markEventAttendance: (eventId, records) => api.post(`/enrollments/events/${eventId}/attendance/`, { records }),
  // Payment accounts
  getPaymentAccounts: (params) => api.get('/enrollments/payment-accounts/', { params }),
  createPaymentAccount: (data) => api.post('/enrollments/payment-accounts/', data),
  updatePaymentAccount: (id, data) => api.patch(`/enrollments/payment-accounts/${id}/`, data),
  deletePaymentAccount: (id) => api.delete(`/enrollments/payment-accounts/${id}/`),
  // Coupons
  getCoupons: (params) => api.get('/enrollments/coupons/', { params }),
  createCoupon: (data) => api.post('/enrollments/coupons/', data),
  updateCoupon: (id, data) => api.patch(`/enrollments/coupons/${id}/`, data),
  deleteCoupon: (id) => api.delete(`/enrollments/coupons/${id}/`),
  validateCoupon: (code, module, subscription) => api.post('/enrollments/coupons/validate_code/', { code, module, subscription }),
  // Revenue
  getPaymentsSummary: () => api.get('/enrollments/payments/summary/'),
  getTeacherAccounting: (teacherId) => api.get('/enrollments/payments/teacher-accounting/', { params: { teacher: teacherId } }),
};

export const messagingService = {
  getGroupChats: (params) => api.get('/messaging/group-chats/', { params }),
  createGroupChat: (data) => api.post('/messaging/group-chats/', data),
  getPrivateConversations: () => api.get('/messaging/private-conversations/'),
  createPrivateConversation: (data) => api.post('/messaging/private-conversations/', data),
  getConversations: () => api.get('/messaging/conversations/'),
  getUnreadCount: () => api.get('/messaging/unread-count/'),
  sendGroupMessage: (chatId, messageData) => api.post(`/messaging/group-chats/${chatId}/messages/`, messageData),
  sendPrivateMessage: (conversationId, messageData) => api.post(`/messaging/private-conversations/${conversationId}/messages/`, messageData),
};

export const ratingService = {
  createRating: (ratingData) => api.post('/ratings/', ratingData),
  updateRating: (id, ratingData) => api.put(`/ratings/${id}/`, ratingData),
  getModuleRatings: (moduleId) => api.get(`/ratings/?module=${moduleId}`),
  reportRating: (data) => api.post('/ratings/reports/', data),
  getRatingReports: (params) => api.get('/ratings/reports/', { params }),
  resolveRatingReport: (id, data) => api.post(`/ratings/reports/${id}/resolve/`, data),
};

export const assignmentService = {
  getAssignments: (lessonId) => api.get(`/assignments/?lesson=${lessonId}`),
  createAssignment: (data) => api.post('/assignments/', data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
  updateAssignment: (id, data) => api.patch(`/assignments/${id}/`, data, data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined),
  deleteAssignment: (id) => api.delete(`/assignments/${id}/`),
  getSubmissions: (assignmentId) => api.get(`/assignments/submissions/?assignment=${assignmentId}`),
  submitAssignment: (formData) => api.post('/assignments/submissions/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteSubmission: (id) => api.delete(`/assignments/submissions/${id}/`),
  gradeSubmission: (id, data) => api.post(`/assignments/submissions/${id}/grade/`, data),
};

export const discussionService = {
  getThreads: (lessonId) => api.get(`/discussions/?lesson=${lessonId}`),
  getThread: (id) => api.get(`/discussions/${id}/`),
  createThread: (data) => api.post('/discussions/', data),
  deleteThread: (id) => api.delete(`/discussions/${id}/`),
  resolveThread: (id, isResolved = true) => api.post(`/discussions/${id}/resolve/`, { is_resolved: isResolved }),
  getReplies: (threadId) => api.get(`/discussions/replies/?thread=${threadId}`),
  createReply: (data) => api.post('/discussions/replies/', data),
  deleteReply: (id) => api.delete(`/discussions/replies/${id}/`),
};

export const pedagogyService = {
  generate: (data) => api.post('/pedagogy/documents/generate/', data, { responseType: 'blob' }),
  listDocuments: (params) => api.get('/pedagogy/documents/', { params }),
  getDocument: (id) => api.get(`/pedagogy/documents/${id}/`),
  updateDocument: (id, data) => api.patch(`/pedagogy/documents/${id}/`, data),
  deleteDocument: (id) => api.delete(`/pedagogy/documents/${id}/`),
  downloadDocument: (id) => api.get(`/pedagogy/documents/${id}/download/`, { responseType: 'blob' }),
  getLabelSettings: () => api.get('/pedagogy/label-settings/'),
  updateLabelSettings: (data) => api.put('/pedagogy/label-settings/', data),
};

export const auditService = {
  getEntries: (params) => api.get('/audit/', { params }),
};

export const instagramCampaignService = {
  login: (username, password) => api.post('/instagram/accounts/login/', { username, password }),
  verifyChallenge: (pendingId, code) => api.post('/instagram/accounts/verify-challenge/', { pending_id: pendingId, code }),
  importSession: (username, sessionData) => api.post('/instagram/accounts/import-session/', { username, session_data: sessionData }),
  getAccounts: () => api.get('/instagram/accounts/'),
  deactivateAccount: (id) => api.post(`/instagram/accounts/${id}/deactivate/`),
  getCampaigns: (params) => api.get('/instagram/campaigns/', { params }),
  getCampaign: (id) => api.get(`/instagram/campaigns/${id}/`),
  createCampaign: (data) => api.post('/instagram/campaigns/', data),
  updateCampaign: (id, data) => api.patch(`/instagram/campaigns/${id}/`, data),
  deleteCampaign: (id) => api.delete(`/instagram/campaigns/${id}/`),
  getMediaOptions: (accountId) => api.get('/instagram/campaigns/media-options/', { params: { account: accountId } }),
  runCampaign: (id) => api.post(`/instagram/campaigns/${id}/run/`),
  getCommentLogs: (campaignId) => api.get('/instagram/comment-logs/', { params: { campaign: campaignId } }),
  getRuns: (campaignId) => api.get('/instagram/runs/', { params: { campaign: campaignId } }),
};

export const blogService = {
  getMyBlog: () => api.get('/blogs/blogs/'),
  requestBlog: () => api.post('/blogs/blogs/', {}),
  updateBlog: (id, data) => api.patch(`/blogs/blogs/${id}/`, data),
  getBlogs: (params) => api.get('/blogs/blogs/', { params }),
  approveBlog: (id) => api.post(`/blogs/blogs/${id}/approve/`),
  rejectBlog: (id, reason) => api.post(`/blogs/blogs/${id}/reject/`, { reason }),
  getPosts: (params) => api.get('/blogs/posts/', { params }),
  createPost: (data) => api.post('/blogs/posts/', data),
  updatePost: (id, data) => api.patch(`/blogs/posts/${id}/`, data),
  deletePost: (id) => api.delete(`/blogs/posts/${id}/`),
  getPages: () => api.get('/blogs/pages/'),
  createPage: (data) => api.post('/blogs/pages/', data),
  updatePage: (id, data) => api.patch(`/blogs/pages/${id}/`, data),
  deletePage: (id) => api.delete(`/blogs/pages/${id}/`),
  addMedia: (postId, file, caption, order) => {
    const fd = new FormData();
    fd.append('post', postId);
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    if (order !== undefined) fd.append('order', order);
    return api.post('/blogs/media/', fd);
  },
  deleteMedia: (id) => api.delete(`/blogs/media/${id}/`),
  uploadAsset: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/blogs/assets/', fd);
  },
  getAssets: () => api.get('/blogs/assets/'),
  deleteAsset: (id) => api.delete(`/blogs/assets/${id}/`),
  getPublicBlog: (subdomain) => api.get(`/blogs/public/${subdomain}/`),
  getComments: (postId) => api.get('/blogs/comments/', { params: { post: postId } }),
  addComment: (postId, content, parent) => api.post('/blogs/comments/', { post: postId, content, parent: parent || null }),
  hideComment: (id) => api.post(`/blogs/comments/${id}/hide/`),
};

export const activityService = {
  getStats: () => api.get('/activity/stats/'),
  getSessions: (params) => api.get('/activity/sessions/', { params }),
  terminateSession: (id) => api.post(`/activity/sessions/${id}/terminate/`),
  getLogs: (params) => api.get('/activity/logs/', { params }),
  getBranding: () => api.get('/activity/branding/'),
  getSettings: () => api.get('/activity/settings/'),
  updateSettings: (data) => {
    const isFile = data.logo instanceof File;
    if (!isFile) return api.patch('/activity/settings/', data);
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        fd.append(key, key === 'social_links' ? JSON.stringify(value) : value);
      }
    });
    return api.patch('/activity/settings/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const announcementService = {
  getAnnouncements: (params) => api.get('/announcements/', { params }),
  createAnnouncement: (data) => api.post('/announcements/', data),
  updateAnnouncement: (id, data) => api.patch(`/announcements/${id}/`, data),
  deleteAnnouncement: (id) => api.delete(`/announcements/${id}/`),
};

export const contactService = {
  sendMessage: (data) => api.post('/contact/', data),
  getMessages: (params) => api.get('/contact/', { params }),
  updateMessage: (id, data) => api.patch(`/contact/${id}/`, data),
  deleteMessage: (id) => api.delete(`/contact/${id}/`),
};

export const feedService = {
  getPosts: (params) => api.get('/feed/', { params }),
  createPost: (data) => {
    const isFile = data.attachment instanceof File;
    if (!isFile) return api.post('/feed/', data);
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') fd.append(key, value);
    });
    return api.post('/feed/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePost: (id) => api.delete(`/feed/${id}/`),
  getComments: (postId) => api.get(`/feed/${postId}/comments/`),
  addComment: (postId, content) => api.post(`/feed/${postId}/comments/`, { content }),
  deleteComment: (id) => api.delete(`/feed/comments/${id}/`),
  reactToPost: (postId) => api.post(`/feed/${postId}/react/`),
  getTeacherEngagementSummary: () => api.get('/feed/teacher-engagement/summary/'),
  getTeacherEngagementUnreadCount: () => api.get('/feed/teacher-engagement/unread-count/'),
  getTeacherEngagementItems: (params) => api.get('/feed/teacher-engagement/items/', { params }),
  markTeacherEngagementSeen: () => api.post('/feed/teacher-engagement/mark-seen/'),
};

export const resourceService = {
  getResources: (params) => api.get('/resources/', { params }),
  createResource: (data) => {
    const isFile = data.file instanceof File;
    if (!isFile) return api.post('/resources/', data);
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') fd.append(key, value);
    });
    return api.post('/resources/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateResource: (id, data) => api.patch(`/resources/${id}/`, data),
  deleteResource: (id) => api.delete(`/resources/${id}/`),
};

export const userService = {
  getDashboard: () => api.get('/users/dashboard/'),
  getMe: () => api.get('/users/me/'),
  getPublicProfile: (id) => api.get(`/users/${id}/public_profile/`),
};

export const deviceService = {
  registerToken: (token, platform) => api.post('/users/devices/', { token, platform }),
  unregisterToken: (token) => api.delete('/users/devices/', { data: { token } }),
};

export const teacherProfileService = {
  list: (params) => api.get('/users/teacher-profiles/', { params }),
  getMine: () => api.get('/users/teacher-profiles/me/'),
  updateMine: (data) => {
    const isFile = data.cover_photo instanceof File || data.signature instanceof File;
    if (!isFile) return api.patch('/users/teacher-profiles/me/', data);
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (key === 'specializations' || key === 'social_links') fd.append(key, JSON.stringify(value));
      else fd.append(key, value);
    });
    return api.patch('/users/teacher-profiles/me/', fd);
  },
};

export const adminService = {
  getMonitoringOverview: () => api.get('/monitoring/overview/'),
  // User management
  getUsers: (params) => api.get('/users/', { params }),
  getUserRelated: (id, params) => api.get(`/users/${id}/related/`, { params }),
  updateUser: (id, data) => api.patch(`/users/${id}/`, data),
  deleteUser: (id, reason) => api.delete(`/users/${id}/`, { params: { reason } }),
  setAdminRole: (id, grant, reason) => api.post(`/users/${id}/set_admin_role/`, { grant, reason }),
  resetUserPassword: (id, data) => api.post(`/users/${id}/reset_password/`, data),
  // Teacher approvals
  getTeacherProfiles: (params) => api.get('/users/teacher-profiles/', { params }),
  approveTeacherProfile: (id) => api.post(`/users/teacher-profiles/${id}/approve/`),
  suspendTeacherProfile: (id, reason) => api.post(`/users/teacher-profiles/${id}/suspend/`, { reason }),
  bulkApproveTeacherProfiles: (ids) => api.post('/users/teacher-profiles/bulk_approve/', { ids }),
  setTeacherFeatured: (id, featured, order = 0) => api.post(`/users/teacher-profiles/${id}/set_featured/`, { featured, order }),
  // Bulk user actions + CSV import/export
  bulkUpdateUsers: (ids, action, reason) => api.post('/users/bulk_update/', { ids, action, reason }),
  exportUsersCsv: (params) => api.get('/users/export_csv/', { params, responseType: 'blob' }),
  importUsersCsv: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/import_csv/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Bulk enrollment approval
  bulkApproveEnrollments: (ids) => api.post('/enrollments/enrollments/bulk_approve/', { ids }),
  // Parent-student links
  getParentLinks: (params) => api.get('/users/parent-links/', { params }),
  approveParentLink: (id) => api.post(`/users/parent-links/${id}/approve/`),
  rejectParentLink: (id) => api.post(`/users/parent-links/${id}/reject/`),
  // Moderation
  getMessageReports: (params) => api.get('/messaging/reports/', { params }),
  resolveMessageReport: (id, data) => api.post(`/messaging/reports/${id}/resolve/`, data),
  getRatingReports: (params) => api.get('/ratings/reports/', { params }),
  resolveRatingReport: (id, data) => api.post(`/ratings/reports/${id}/resolve/`, data),
  // Subscriptions
  getSubscriptions: (params) => api.get('/enrollments/subscriptions/', { params }),
  createSubscription: (data) => api.post('/enrollments/subscriptions/', data),
  updateSubscription: (id, data) => api.patch(`/enrollments/subscriptions/${id}/`, data),
  deleteSubscription: (id) => api.delete(`/enrollments/subscriptions/${id}/`),
  // Grades
  getGrades: () => api.get('/academics/grades/'),
  // Attendance reports
  getAttendance: (params) => api.get('/enrollments/attendance/', { params }),
  // Enrollments (admin sees all)
  getEnrollments: (params) => api.get('/enrollments/enrollments/', { params }),
  // Payments
  getPayments: (params) => api.get('/enrollments/payments/', { params }),
  createPayment: (data) => api.post('/enrollments/payments/', data),
  updatePayment: (id, data) => api.patch(`/enrollments/payments/${id}/`, data),
  deletePayment: (id, reason) => api.delete(`/enrollments/payments/${id}/`, { params: { reason } }),
};

export const tenancyService = {
  resolveHost: () => api.get('/platform/resolve/'),
};

export const platformService = {
  getOverview: () => api.get('/platform/overview/'),
  getEntitlements: () => api.get('/platform/entitlements/'),
  getSchools: (params) => api.get('/platform/schools/', { params }),
  getSchool: (id) => api.get(`/platform/schools/${id}/`),
  createSchool: (data) => api.post('/platform/schools/', data),
  updateSchool: (id, data) => api.patch(`/platform/schools/${id}/`, data),
  activateSchool: (id) => api.post(`/platform/schools/${id}/activate/`),
  suspendSchool: (id) => api.post(`/platform/schools/${id}/suspend/`),
  archiveSchool: (id) => api.post(`/platform/schools/${id}/archive/`),
  retryProvisioning: (id, data) => api.post(`/platform/schools/${id}/retry-provisioning/`, data),
  migrateSchool: (id) => api.post(`/platform/schools/${id}/migrate/`),
  backupSchool: (id) => api.post(`/platform/schools/${id}/backup/`),
  restoreSchoolBackup: (id, backupId, confirmation) => api.post(`/platform/schools/${id}/backups/${backupId}/restore/`, { confirmation }),
  checkSchoolHealth: (id) => api.post(`/platform/schools/${id}/health/`),
  verifyDomain: (id) => api.post(`/platform/schools/${id}/verify-domain/`),
  applyPlan: (id, planId) => api.post(`/platform/schools/${id}/apply-plan/`, { plan_id: planId }),
  getPlans: () => api.get('/platform/plans/', { params: { page_size: 100 } }),
  createPlan: (data) => api.post('/platform/plans/', data),
  updatePlan: (id, data) => api.patch(`/platform/plans/${id}/`, data),
  deletePlan: (id) => api.delete(`/platform/plans/${id}/`),
};

export const parentService = {
  getLinks: () => api.get('/users/parent-links/'),
  requestLink: (data) => api.post('/users/parent-links/', data),
  approveLink: (id) => api.post(`/users/parent-links/${id}/approve/`),
  rejectLink: (id) => api.post(`/users/parent-links/${id}/reject/`),
  createInvitation: (data) => api.post('/users/family-invitations/', data),
  redeemInvitation: (token) => api.post('/users/family-invitations/redeem/', { token }),
  getInvitations: () => api.get('/users/family-invitations/'),
  revokeInvitation: (id) => api.post(`/users/family-invitations/${id}/revoke/`),
  getDashboard: () => api.get('/users/dashboard/'),
  getChildEnrollments: (studentId) => api.get(`/enrollments/enrollments/?student=${studentId}`),
  getChildProgress: (studentId) => api.get(`/content/lesson-progress/?student=${studentId}`),
  getChildQuizAttempts: (studentId) => api.get(`/quizzes/attempts/?student=${studentId}`),
  getChildAttendance: (studentId) => api.get(`/enrollments/attendance/?student=${studentId}`),
  getChildPayments: (studentId) => api.get(`/enrollments/payments/?student=${studentId}`),
  // AssignmentSubmissionViewSet has no ?student= filter for a parent (it
  // always returns every approved child's submissions combined) — callers
  // that need just one child filter the result client-side.
  getAssignmentSubmissions: () => api.get('/assignments/submissions/'),
};
